import { defineStore } from 'pinia';
import type { JobBoard, Category, AllowedCompany } from '@/core/collections/job-boards/job-board.interface';
import type { Language } from '@future-harry/nxt-vue-library';
import type { AvailableFilter } from '@/modules/content-blocks/interfaces/jobs-block.interface';
import { createDefaultJobBoard } from '@/core/collections/job-boards/job-board.helper';
import { jobBoardI18n } from '@/i18n';

export const useJobBoardStore = defineStore('jobBoard', {
   state: () => ({
      currentJobBoard: createDefaultJobBoard() as JobBoard,
      activeLanguage: null as Language | null,
      activeLanguageName: 'en' as string,
      availableFilters: { filter_elements: [] } as { filter_elements: AvailableFilter[] },
      isFetchFinished: false,
      isUpdatingJobBoard: false,
      fetchError: null as Error | null,
   }),

   getters: {
      categories: (state) => state.currentJobBoard.categories ?? [],

      categoryImages: (state) => {
         return (state.currentJobBoard.categories ?? [])
            .filter((category: Category) => category.image_id) // v-model:media-id="jobCategory.image_id"
            .map((category: Category) => ({
               category_id: category.id,
               image_id: category.image_id,
            }));
      },

      theme: (state) => {
         if (!state.currentJobBoard) return {};

         return {
            border_radius: state.currentJobBoard.border_radius,
            primary_color: state.currentJobBoard.primary_color,
            primary_font_color: state.currentJobBoard.primary_font_color,
            secondary_color: state.currentJobBoard.secondary_color,
            secondary_font_color: state.currentJobBoard.secondary_font_color,
            font_family: state.currentJobBoard.font_family,
         };
      },
   },

   actions: {
      setJobBoard(jobBoard: JobBoard) {
         this.currentJobBoard = jobBoard;

         // set active language, preferring localStorage over the default language
         if (jobBoard.languages && jobBoard.languages.length > 0) {
            const savedLocale = window.localStorage.getItem('locale_board');
            const savedLanguage = savedLocale ? jobBoard.languages.find((lang) => lang.iso_639_1 === savedLocale) : null;
            const defaultLanguage = jobBoard.languages.find((lang) => lang.id === jobBoard.default_language_id) ?? jobBoard.languages[0];

            const language = savedLanguage ?? defaultLanguage;

            if (import.meta.env.MODE === 'public') {
               // LS > default for public app
               this.setActiveLanguage(language);
            } else {
               // Default for admin app
               this.setActiveLanguage(defaultLanguage);
            }
         }
      },

      setCustomers(allowedCompanies: AllowedCompany[]) {
         this.currentJobBoard.customers = allowedCompanies;
      },

      setFilters(filters: { filter_elements: AvailableFilter[] }) {
         this.availableFilters = filters;
      },

      setActiveLanguage(language: Language) {
         this.activeLanguage = language;
         this.activeLanguageName = language.iso_639_1;
         window.localStorage.setItem('locale_board', language.iso_639_1);

         // @ts-ignore
         jobBoardI18n.global.locale.value = language.iso_639_1;
      },
   },
});
